import React, { useMemo } from "react";
import clsx from "clsx";
import { Link } from "react-router";
import styles from "./PlanningWeek.module.scss";
import useSwipe from "./useSwipe";
import { round } from "../../util/util";
import { addWorker, hasText, shownWorkers, siteDayDetails } from "./planningData";
import SiteDayDetails from "./SiteDayDetails";

const formatHours = hours => round(hours, 1) + "h";

/**
 * One day as a list, like the iOS calendar day view: the total, the workers with their hours across the sites
 * and the events without site first, then the sites with something that day (workers with hours or an alert, infos or report of a prestation, events),
 * those with workers first; a site opens its details, an event its editor.
 * Swipe to go to the next or previous day.
 */
const PlanningDayList = ({
  dayKey,
  sites,
  events,
  prestations,
  workersById,
  openEvent,
  onPreviousDay,
  onNextDay
}) => {
  const {offset, swiping, handlers} = useSwipe({onSwipeLeft: onNextDay, onSwipeRight: onPreviousDay});

  const day = useMemo(() => {
    const dayEvents = events.filter(event => event.date === dayKey);
    const dayPrestations = prestations.filter(prestation => prestation.date === dayKey);

    const siteRows = sites
      .map(site => {
        const {workers, notes} = siteDayDetails(
          dayPrestations.filter(prestation => prestation.siteId === site.id),
          workersById
        );

        return {
          site,
          workers,
          hours: workers.reduce((acc, worker) => acc + worker.hours, 0),
          notes,
          events: dayEvents.filter(event => event.siteId === site.id),
          isActive: site.dateStart <= dayKey && dayKey <= site.activeEnd
        };
      })
      // Only the sites with something to show that day
      .filter(row => row.workers.length > 0 || row.notes.length > 0 || row.events.length > 0)
      // Sites with prestations first, each group keeping the order of the sites
      .sort((a, b) => (b.workers.length > 0) - (a.workers.length > 0));

    // Each worker once, with the hours of all their sites
    const workers = new Map();

    siteRows.forEach(row => row.workers.forEach(worker => addWorker(workers, worker)));

    return {
      siteRows,
      workers: shownWorkers(workers, workersById),
      customEvents: dayEvents.filter(event => !event.siteId),
      totalHours: siteRows.reduce((acc, row) => acc + row.hours, 0)
    };
  }, [dayKey, sites, events, prestations, workersById]);

  return <div className={styles.dayList} {...handlers}>
    <div className={clsx(styles.dayListContent, swiping && styles.swiping)}
         style={{transform: "translateX(" + offset + "px)"}}>
      <div className={clsx(styles.dayCard, styles.dayCardGlobal)}>
        <div className={styles.dayCardHeader}>
          <span className={styles.dayCardTitle}>Journée</span>
          {day.totalHours > 0 && <span className={styles.dayCardHours}>{formatHours(day.totalHours)}</span>}
        </div>

        <SiteDayDetails workers={day.workers} notes={[]}/>

        {day.customEvents.map(event => <div key={event.id}
                                            className={styles.dayCardEvent}
                                            onClick={() => openEvent(event, null)}>
          {event.name}
        </div>)}
      </div>

      {day.siteRows.map((row, i) => {
        const siteColor = row.site.color.split("|");
        const firstWithout = i > 0 && day.siteRows[i - 1].workers.length > 0 && row.workers.length === 0;

        return <React.Fragment key={row.site.id}>
          {firstWithout && <div className={styles.dayDivider}/>}

          <Link to={"/sites/" + row.site.id}
                className={clsx(styles.dayCard, !row.isActive && styles.inactive)}
                style={{borderLeftColor: siteColor[0]}}>
            <div className={styles.dayCardHeader}>
              <span className={styles.dayCardTitle}>{row.site.name}</span>
              {row.hours > 0 && <span className={styles.dayCardHours}>{formatHours(row.hours)}</span>}
            </div>

            <SiteDayDetails workers={row.workers} notes={row.notes}/>

            {row.events.map(event => <div key={event.id}
                                          className={styles.dayCardEvent}
                                          onClick={e => {
                                            // The event opens its editor, not the site of the card
                                            e.preventDefault();
                                            openEvent(event, row.site);
                                          }}>
              {event.name}
              {hasText(event.description) && <div className={styles.richText}
                                                    dangerouslySetInnerHTML={{__html: event.description}}/>}
            </div>)}
          </Link>
        </React.Fragment>;
      })}

      {day.siteRows.length === 0 && <div className={styles.dayEmpty}>Aucune prestation ni événement ce jour</div>}
    </div>
  </div>;
};

export default PlanningDayList;
